import React from 'react';

import type {ITimeSlot} from '../../types/ITimeSlot';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {parse, addMinutes} from 'date-fns';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {BodyMediumBold} from '@spaced-out/ui-design-system/lib/components/Text';

import {
  selectCurrentDateSlots,
  selectSelectedTimeSlot,
  selectEventTemplate,
  selectCurrentSequenceNumber,
} from '../../selectors/booking-selector';
import {selectTimezone} from '../../selectors/timezoneSelectors';
import {formatAvailabilityTime} from '../../utils/booking';
import {useRootMachine} from '../../hooks/useRootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {calculateDayDuration} from '../../utils/sequential';

import {classify} from '@spaced-out/ui-design-system/lib/utils/classify';
import {Row, Col} from '@spaced-out/ui-design-system/lib/components/Grid';

import css from './booking-timeslot-picker.css';


export const BookingTimeslotPicker = () => {
  const {language, t, makeKey} = useLabelTranslation();

  const {rootActor, rootState} = useRootMachine();
  const {bookingActor} = useBookingMachine();
  const isRescheduleFlow = rootState.matches('rescheduleFlow');
  const availableTimeSlots = useSelector(bookingActor, selectCurrentDateSlots);
  const selectedTimeSlot = useSelector(bookingActor, selectSelectedTimeSlot);
  const timezone = useSelector(rootActor, selectTimezone);
  const eventTemplate = useSelector(bookingActor, selectEventTemplate);
  const currentSequenceNumber = useSelector(
    bookingActor,
    selectCurrentSequenceNumber,
  );
  const isSequentialTemplate = Boolean(
    eventTemplate?.distribution_rule === 'sequential_interview',
  );

  const handleTimeSlotSelect = (timeSlot: ITimeSlot) => {
    bookingActor.send({type: 'timeslot.confirm', timeSlot});
  };

  // Calculate total duration for the current day in sequential template
  const dayDuration = React.useMemo((): number => {
    if (!isSequentialTemplate) {
      return 0;
    }
    return calculateDayDuration(eventTemplate, currentSequenceNumber);
  }, [isSequentialTemplate, eventTemplate, currentSequenceNumber]);

  const formatTimeSlotDisplay = React.useCallback(
    (timeSlot: ITimeSlot): string => {
      if (isSequentialTemplate && dayDuration > 0) {
        const totalMinutes = dayDuration;
        const startTime = parse(timeSlot.start_time, 'HH:mm', new Date());
        const endTime = addMinutes(startTime, totalMinutes);
        const startFormatted = formatAvailabilityTime(
          timeSlot.start_time,
          language,
        );
        const endFormatted = formatAvailabilityTime(
          `${endTime.getHours().toString().padStart(2, '0')}:${endTime
            .getMinutes()
            .toString()
            .padStart(2, '0')}`,
          language,
        );
        return `${startFormatted} - ${endFormatted}`;
      }
      return formatAvailabilityTime(timeSlot.start_time, language);
    },
    [isSequentialTemplate, dayDuration, language],
  );

  return (
    <div
      className={classify(css.bookingTimeslotPickerContainer, {
        [css.bookingFlow]: !isRescheduleFlow,
      })}
    >
      <div className={css.bookingTimeslotPickerHeader}>
        <BodyMediumBold>
          {t('Select_a_time_slot', 'Select a time slot')}
        </BodyMediumBold>
        <BodyMediumBold>
          {timezone ? t(makeKey(timezone), timezone) : ''}
        </BodyMediumBold>
      </div>
      <Row
        gridType="autoFill"
        className={classify(css.timeSlotButtonContainer, {
          [css.timeSlotButtonContainerSequential]: isSequentialTemplate,
        })}
      >
        {Array.isArray(availableTimeSlots) &&
          availableTimeSlots.map((timeSlot: ITimeSlot) => (
            <Col key={timeSlot.start_time} className={css.timeSlotButtonItem}>
              <Button
                size="small"
                type="tertiary"
                key={timeSlot.start_time}
                classNames={{
                  wrapper: classify(css.timeSlotButton, {
                    [css.timeSlotButtonSelected]:
                      selectedTimeSlot?.start_time === timeSlot.start_time,
                  }),
                }}
                onClick={() => handleTimeSlotSelect(timeSlot)}
              >
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                {formatTimeSlotDisplay(timeSlot)}
              </Button>
            </Col>
          ))}
      </Row>
    </div>
  );
};
